import React, {useContext, useMemo} from 'react';
import Translate from '@src/components/Translate/Translate';
import {useFormContext, useWatch} from 'react-hook-form';
import {IFormFieldItemPropsContext} from '@src/@interfaces/contexts/IFormFieldItemPropsContext';
import {IOrderModel} from '@src/@interfaces/models/Order/IOrderModel';
import InlineUserRepresentation from '@src/components/CommentsList/InlineUserRepresentation';
import {FormFieldDesignContext} from '@src/contexts/FormFieldDesignContext';
import classnames from '@src/helpers/classnames';
import {LinkToRelationFormPage} from '@src/components/Form/LinkToRelationFormPage';
import RequiredFieldSign from '@src/components/Form/RequiredFieldSign';

export default function OrderProductDetails(props: {
    model?: IFormFieldItemPropsContext<IOrderModel>['model'],
    modelOptions?: IFormFieldItemPropsContext<IOrderModel>['modelOptions'],
    managerSlot?: React.ReactNode,
    clientSlot?: React.ReactNode,
    labelSize?: 'sm' | 'lg',
    withoutShiftOfTop?: boolean,
}) {
    const {model, modelOptions, clientSlot, managerSlot, labelSize, withoutShiftOfTop = false} = props;
    const {control} = useFormContext();
    const [design] = useContext(FormFieldDesignContext);

    const modelManager = model?.manager;
    const modelClient = model?.client;

    const managerId = useWatch({control, name: 'manager'}) ?? model?.manager_id ?? modelManager?.id;
    const clientId = useWatch({control, name: 'client'}) ?? model?.client_id ?? modelClient?.id;

    const [manager, client] = useMemo(() => {
        const manager = () => {
            if (managerSlot) {
                return managerSlot;
            }

            const manager = (modelOptions?.manager ?? []).find(
                    ({value}) => value === managerId
            ) ?? modelManager;

            return manager ? <InlineUserRepresentation
                    user={manager}
                    tag="span"
                    className="comment-list__item__author"
            /> : '—';
        };

        const client = () => {
            if (clientSlot) {
                return clientSlot;
            }

            return (modelOptions?.client ?? []).find(
                    ({value}) => value === clientId
            )?.label ?? modelClient?.title ?? '—';
        };

        return [manager(), client()];
    }, [managerSlot, modelOptions?.manager, modelOptions?.client, modelManager, managerId, clientSlot, modelClient?.title, clientId]);

    return <>
        <div className="mb-3">
            <LinkToRelationFormPage relationModel="User" modelId={managerId}>
                <label className={classnames('col-form-label', {
                    [`col-form-label-${labelSize ?? design.size}`]: labelSize ?? design.size,
                    'pt-0': withoutShiftOfTop,
                })}>
                    <Translate message="OrderForm.products.details.manager" tag="span"/>
                </label>
            </LinkToRelationFormPage>
            <div>{manager}</div>
        </div>

        <div className="mb-3">
            <LinkToRelationFormPage relationModel="Client" modelId={clientId}>
                <RequiredFieldSign displaySign={!props.model?.id}>
                    <label className={classnames('col-form-label', {
                        [`col-form-label-${labelSize ?? design.size}`]: labelSize ?? design.size,
                    })}>
                        <Translate message="OrderForm.products.details.client" tag="span"/>
                    </label>
                </RequiredFieldSign>
            </LinkToRelationFormPage>
            <div>{client}</div>
        </div>
    </>;
}
